import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const HEARTBEAT_MS = 10_000;
const MIN_FLUSH_SECONDS = 5;

/**
 * Tracks active read time for an article. Only counts seconds while:
 *  - the tab is visible
 *  - the window has focus
 *
 * Flushes accumulated seconds every 10s (heartbeat) and on unmount/visibility hidden
 * via sendBeacon for reliability.
 */
export function useArticleReadTime(slug: string | undefined) {
  const { user } = useAuth();
  const accumulatedRef = useRef(0);
  const lastTickRef = useRef<number | null>(null);
  const userIdRef = useRef<string | null>(null);

  // Keep latest user id available to flush() without re-binding listeners.
  useEffect(() => {
    userIdRef.current = user?.id ?? null;
  }, [user]);

  useEffect(() => {
    if (!slug) return;

    const isActive = () => document.visibilityState === 'visible' && document.hasFocus();

    const tick = () => {
      if (!isActive()) {
        lastTickRef.current = null;
        return;
      }
      const now = Date.now();
      if (lastTickRef.current != null) {
        const delta = (now - lastTickRef.current) / 1000;
        // cap any single delta at 30s to avoid sleep/wake jumps
        accumulatedRef.current += Math.min(delta, 30);
      }
      lastTickRef.current = now;
    };

    const flush = (useBeacon = false) => {
      tick();
      const seconds = Math.floor(accumulatedRef.current);
      if (seconds < MIN_FLUSH_SECONDS) return;
      accumulatedRef.current -= seconds;

      const payload = {
        content_slug: slug,
        seconds,
        user_id: userIdRef.current ?? undefined,
      };

      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-article-read-time`;
      if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
        try {
          const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
          navigator.sendBeacon(url, blob);
          return;
        } catch {
          /* fall through to invoke */
        }
      }
      supabase.functions.invoke('track-article-read-time', { body: payload }).catch(() => {});
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        flush(true);
      } else {
        lastTickRef.current = Date.now();
      }
    };
    const onBlur = () => { tick(); lastTickRef.current = null; };
    const onFocus = () => { lastTickRef.current = Date.now(); };
    const onPageHide = () => flush(true);

    if (isActive()) lastTickRef.current = Date.now();
    const interval = window.setInterval(() => { tick(); flush(); }, HEARTBEAT_MS);

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pagehide', onPageHide);
      flush(true);
    };
  }, [slug]);
}
